import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { PAYABLE_STATUSES } from "@shared/finance/supplierInvoices";
import type { SupplierInvoiceStatus } from "@shared/finance/supplierInvoices";
import { Plus, XCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface Payment {
  id: number;
  paymentDate: string;
  amount: string;
  reference?: string | null;
  notes?: string | null;
  deletedAt?: string | null;
}

interface SupplierInvoicePaymentsProps {
  invoiceId: number;
  status: SupplierInvoiceStatus;
  payments: Payment[];
  canWrite: boolean;
}

function formatEUR(amount: string | number) {
  const n = typeof amount === "string" ? parseFloat(amount) : amount;
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString("es-ES");
}

export function SupplierInvoicePayments({ invoiceId, status, payments, canWrite }: SupplierInvoicePaymentsProps) {
  const utils = trpc.useUtils();
  const canAddPayment = canWrite && PAYABLE_STATUSES.has(status);

  const [addOpen, setAddOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidPaymentId, setVoidPaymentId] = useState<number | null>(null);
  const [voidReason, setVoidReason] = useState("");
  const [form, setForm] = useState({ paymentDate: "", amount: "", reference: "", notes: "" });

  const addPayment = trpc.supplierInvoices.addPayment.useMutation({
    onSuccess: () => {
      toast.success("Pago registrado");
      utils.supplierInvoices.getById.invalidate({ invoiceId });
      utils.supplierInvoices.listPayments.invalidate({ invoiceId });
      utils.supplierInvoices.paymentSummary.invalidate({ invoiceId });
      setAddOpen(false);
      setForm({ paymentDate: "", amount: "", reference: "", notes: "" });
    },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  const voidPayment = trpc.supplierInvoices.voidPayment.useMutation({
    onSuccess: () => {
      toast.success("Pago anulado");
      utils.supplierInvoices.getById.invalidate({ invoiceId });
      utils.supplierInvoices.listPayments.invalidate({ invoiceId });
      utils.supplierInvoices.paymentSummary.invalidate({ invoiceId });
      setVoidOpen(false);
      setVoidPaymentId(null);
      setVoidReason("");
    },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  function handleAddSubmit(e: React.FormEvent) {
    e.preventDefault();
    addPayment.mutate({
      invoiceId,
      paymentDate: form.paymentDate,
      amount: form.amount,
      reference: form.reference || undefined,
      notes: form.notes || undefined,
    });
  }

  function openVoid(paymentId: number) {
    setVoidPaymentId(paymentId);
    setVoidReason("");
    setVoidOpen(true);
  }

  function handleVoidSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!voidPaymentId || !voidReason.trim()) return;
    voidPayment.mutate({ paymentId: voidPaymentId, reason: voidReason });
  }

  const active = payments.filter((p) => !p.deletedAt);
  const voided = payments.filter((p) => p.deletedAt);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {active.length} {active.length === 1 ? "pago activo" : "pagos activos"}
        </span>
        {canAddPayment && (
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Registrar pago
          </Button>
        )}
        {!PAYABLE_STATUSES.has(status) && canWrite && (
          <p className="text-xs text-muted-foreground">
            Solo se pueden registrar pagos en facturas aprobadas o programadas.
          </p>
        )}
      </div>

      {active.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">Sin pagos registrados.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fecha</TableHead>
              <TableHead className="text-right">Importe</TableHead>
              <TableHead>Referencia</TableHead>
              <TableHead>Notas</TableHead>
              {canWrite && <TableHead />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {active.map((p) => (
              <TableRow key={p.id}>
                <TableCell>{formatDate(p.paymentDate)}</TableCell>
                <TableCell className="text-right font-medium">{formatEUR(p.amount)}</TableCell>
                <TableCell>{p.reference ?? "-"}</TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{p.notes ?? "-"}</TableCell>
                {canWrite && (
                  <TableCell>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive"
                      onClick={() => openVoid(p.id)}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Anular
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {voided.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">Pagos anulados</p>
          <Table>
            <TableBody>
              {voided.map((p) => (
                <TableRow key={p.id} className="opacity-50">
                  <TableCell>{formatDate(p.paymentDate)}</TableCell>
                  <TableCell className="text-right line-through">{formatEUR(p.amount)}</TableCell>
                  <TableCell>{p.reference ?? "-"}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">Anulado</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add payment dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Registrar pago</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="pay-date">Fecha de pago *</Label>
              <Input
                id="pay-date"
                type="date"
                value={form.paymentDate}
                onChange={(e) => setForm((p) => ({ ...p, paymentDate: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pay-amount">Importe *</Label>
              <Input
                id="pay-amount"
                type="number"
                step="0.01"
                min="0.01"
                value={form.amount}
                onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pay-ref">Referencia</Label>
              <Input
                id="pay-ref"
                value={form.reference}
                onChange={(e) => setForm((p) => ({ ...p, reference: e.target.value }))}
                placeholder="Nº transferencia, etc."
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pay-notes">Notas</Label>
              <Textarea
                id="pay-notes"
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={addPayment.isPending}>
                {addPayment.isPending ? "Guardando..." : "Registrar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Void payment dialog */}
      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Anular pago</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleVoidSubmit} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Esta acción anulará el pago. Por favor, indica el motivo.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="void-reason">Motivo *</Label>
              <Textarea
                id="void-reason"
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                required
                minLength={1}
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setVoidOpen(false)}>Cancelar</Button>
              <Button type="submit" variant="destructive" disabled={voidPayment.isPending || !voidReason.trim()}>
                {voidPayment.isPending ? "Anulando..." : "Anular pago"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
